import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Stack,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Snackbar,
  IconButton,
  Paper,
  Divider,
  LinearProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import {
  Add as AddIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { format } from 'date-fns'
import { ja, enUS } from 'date-fns/locale'

interface BillingPlan {
  id: string
  planType: string
  name: string
  nameJa: string
}

interface TenantSubscription {
  id: string
  status: string
  startDate: string
  endDate: string | null
  billingPlan: BillingPlan
}

interface Tenant {
  id: string
  name: string
  slug: string
  domain: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  _count?: {
    users: number
    complianceRules?: number
    tests?: number
  }
  subscription?: TenantSubscription | null
}

// 課金請求用統計の型定義
interface TenantStatsSummary {
  tenantId: string
  tenantName: string
  tenantSlug: string
  isActive: boolean
  createdAt: string
  summary: {
    users: {
      total: number
      active: number
    }
    complianceRules: number
    tests: number
    enrollments: {
      total: number
      completed: number
      pending: number
      overdue: number
    }
    testResults: number
  }
}

interface GlobalStatsSummary {
  totalTenants: number
  activeTenants: number
  totalUsers: number
  totalActiveUsers: number
  totalComplianceRules: number
  totalTests: number
  totalEnrollments: number
  totalTestResults: number
  calculatedAt: string
}

interface AllTenantsStatsResponse {
  globalSummary: GlobalStatsSummary
  tenants: TenantStatsSummary[]
}

interface TenantFormData {
  name: string
  slug: string
  description: string
  isActive: boolean
  createAdmin: boolean
  adminEmail: string
  adminName: string
  adminDepartment: string
  adminPosition: string
}

const TenantManagementPage: React.FC = () => {
  const { t, i18n } = useTranslation('tenant')
  const locale = i18n.language === 'ja' ? ja : enUS
  const { token } = useAuth()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    slug: '',
    description: '',
    isActive: true,
    createAdmin: false,
    adminEmail: '',
    adminName: '',
    adminDepartment: '',
    adminPosition: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // 統計関連のstate
  const [statsData, setStatsData] = useState<AllTenantsStatsResponse | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [showStats, setShowStats] = useState(false)

  // プラン変更関連のstate
  const [billingPlans, setBillingPlans] = useState<BillingPlan[]>([])
  const [planDialogOpen, setPlanDialogOpen] = useState(false)
  const [selectedPlanTenant, setSelectedPlanTenant] = useState<Tenant | null>(null)
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')

  // テナント一覧取得
  const fetchTenants = useCallback(async () => {
    try {
      const response = await fetch('/api/tenants', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(t('messages.fetchError'))
      }

      const data = await response.json()
      setTenants(data.tenants)
    } catch (error) {
      console.error('テナント取得エラー:', error)
      setError(error instanceof Error ? error.message : t('messages.fetchFailed'))
    } finally {
      setLoading(false)
    }
  }, [token, t])

  useEffect(() => {
    fetchTenants()
  }, [fetchTenants])

  // 統計データ取得
  const fetchStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const response = await fetch('/api/tenants/stats', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(t('messages.statsFetchError'))
      }

      const data: AllTenantsStatsResponse = await response.json()
      setStatsData(data)
    } catch (error) {
      console.error('統計取得エラー:', error)
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : t('messages.statsFetchFailed'),
        severity: 'error',
      })
    } finally {
      setStatsLoading(false)
    }
  }, [token, t])

  // 統計表示トグル時にデータ取得
  useEffect(() => {
    if (showStats && !statsData && !statsLoading) {
      fetchStats()
    }
  }, [showStats, statsData, statsLoading, fetchStats])

  // 課金プラン一覧取得
  const fetchBillingPlans = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/plans', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('プラン取得に失敗しました')
      }

      const data = await response.json()
      if (data.success && data.data) {
        setBillingPlans(data.data)
      }
    } catch (error) {
      console.error('プラン取得エラー:', error)
    }
  }, [token])

  // 初期ロード時にプラン一覧も取得
  useEffect(() => {
    fetchBillingPlans()
  }, [fetchBillingPlans])

  // プラン変更ダイアログを開く
  const handleOpenPlanDialog = (tenant: Tenant) => {
    setSelectedPlanTenant(tenant)
    setSelectedPlanId(tenant.subscription?.billingPlan?.id || '')
    setPlanDialogOpen(true)
  }

  // プラン変更を実行
  const handleUpdatePlan = async () => {
    if (!selectedPlanTenant || !selectedPlanId) {
      setSnackbar({ open: true, message: t('messages.selectPlan'), severity: 'error' })
      return
    }

    setSubmitting(true)
    try {
      const response = await fetch(`/api/tenants/${selectedPlanTenant.id}/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ billingPlanId: selectedPlanId }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || t('messages.planUpdateFailed'))
      }

      setSnackbar({ open: true, message: t('messages.planUpdateSuccess'), severity: 'success' })
      setPlanDialogOpen(false)
      setSelectedPlanTenant(null)
      setSelectedPlanId('')
      fetchTenants() // リストを更新
    } catch (error) {
      console.error('プラン更新エラー:', error)
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : t('messages.planUpdateFailed'),
        severity: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  // スラッグ自動生成
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: prev.slug === '' ? generateSlug(name) : prev.slug, // スラッグが空の場合のみ自動生成
    }))
  }

  // テナント作成
  const handleCreateTenant = async () => {
    if (!formData.name.trim() || !formData.slug.trim()) {
      setSnackbar({ open: true, message: t('messages.nameSlugRequired'), severity: 'error' })
      return
    }

    // CORPORATE_ADMIN作成が指定されている場合の追加バリデーション
    if (formData.createAdmin) {
      if (!formData.adminEmail.trim() || !formData.adminName.trim()) {
        setSnackbar({ open: true, message: t('messages.adminInfoRequired'), severity: 'error' })
        return
      }
    }

    setSubmitting(true)
    try {
      const response = await fetch('/api/tenants', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || t('messages.createFailed'))
      }

      const result = await response.json()
      let successMessage = t('messages.createSuccess')
      if (result.adminCreated) {
        successMessage += `\n${t('messages.adminCreated')}:\n${t('messages.adminEmail')}: ${result.adminEmail}\n${t('messages.adminPassword')}: ${result.adminPassword}`
      }

      setSnackbar({ open: true, message: successMessage, severity: 'success' })
      setCreateDialogOpen(false)
      setFormData({
        name: '',
        slug: '',
        description: '',
        isActive: true,
        createAdmin: false,
        adminEmail: '',
        adminName: '',
        adminDepartment: '',
        adminPosition: '',
      })
      fetchTenants() // リストを更新
    } catch (error) {
      console.error('テナント作成エラー:', error)
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : t('messages.createFailed'),
        severity: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  // テナント更新
  const handleUpdateTenant = async () => {
    if (!selectedTenant || !formData.name.trim()) {
      setSnackbar({ open: true, message: t('messages.nameRequired'), severity: 'error' })
      return
    }

    setSubmitting(true)
    try {
      const response = await fetch(`/api/tenants/${selectedTenant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          isActive: formData.isActive,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || t('messages.updateFailed'))
      }

      setSnackbar({ open: true, message: t('messages.updateSuccess'), severity: 'success' })
      setEditDialogOpen(false)
      setSelectedTenant(null)
      setFormData({
        name: '',
        slug: '',
        description: '',
        isActive: true,
        createAdmin: false,
        adminEmail: '',
        adminName: '',
        adminDepartment: '',
        adminPosition: '',
      })
      fetchTenants() // リストを更新
    } catch (error) {
      console.error('テナント更新エラー:', error)
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : t('messages.updateFailed'),
        severity: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    setFormData({
      name: tenant.name,
      slug: tenant.slug,
      description: tenant.domain || '',
      isActive: tenant.isActive,
      createAdmin: false,
      adminEmail: '',
      adminName: '',
      adminDepartment: '',
      adminPosition: '',
    })
    setEditDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setCreateDialogOpen(false)
    setEditDialogOpen(false)
    setSelectedTenant(null)
    setFormData({
      name: '',
      slug: '',
      description: '',
      isActive: true,
      createAdmin: false,
      adminEmail: '',
      adminName: '',
      adminDepartment: '',
      adminPosition: '',
    })
  }

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  // 完了率を計算するヘルパー関数
  const getCompletionRate = (completed: number, total: number): number => {
    if (total === 0) return 0
    return Math.round((completed / total) * 100)
  }

  return (
    <Box>
      {/* タイトル行: タイトル + 右ボタン */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('title')}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant={showStats ? 'contained' : 'outlined'}
            startIcon={<BarChartIcon />}
            onClick={() => setShowStats(!showStats)}
            color={showStats ? 'primary' : 'inherit'}
          >
            {showStats ? t('actions.hideStats') : t('actions.showStats')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('actions.create')}
          </Button>
        </Stack>
      </Box>

      {/* 課金統計セクション */}
      {showStats && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <TrendingUpIcon color="primary" />
            {t('stats.title')}
          </Typography>

          {statsLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>{t('stats.loading')}</Typography>
            </Box>
          ) : statsData ? (
            <>
              {/* グローバルサマリー */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  {t('stats.globalSummary')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, 1fr)',
                      sm: 'repeat(4, 1fr)',
                    },
                    gap: 2,
                  }}
                >
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {statsData.globalSummary.totalTenants}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('stats.tenantCount')}
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {statsData.globalSummary.totalActiveUsers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('stats.activeUsers')}
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="secondary.main">
                      {statsData.globalSummary.totalEnrollments}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('stats.totalEnrollments')}
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="secondary.main">
                      {statsData.globalSummary.totalTestResults}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('stats.testCount')}
                    </Typography>
                  </Paper>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  {t('stats.lastUpdated')}:{' '}
                  {format(new Date(statsData.globalSummary.calculatedAt), 'yyyy/MM/dd HH:mm:ss', {
                    locale,
                  })}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* テナント別詳細テーブル */}
              <Typography variant="h6" gutterBottom color="text.secondary">
                {t('stats.tenantDetails')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('table.tenantName')}</TableCell>
                      <TableCell align="center">{t('table.status')}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('tooltips.usersCount')}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                            }}
                          >
                            <PeopleIcon fontSize="small" />
                            {t('table.users')}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('tooltips.rulesCount')}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                            }}
                          >
                            <AssignmentIcon fontSize="small" />
                            {t('table.rules')}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('tooltips.testsCount')}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                            }}
                          >
                            <QuizIcon fontSize="small" />
                            {t('table.tests')}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('tooltips.enrollmentDetails')}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                            }}
                          >
                            <ScheduleIcon fontSize="small" />
                            {t('table.enrollmentStatus')}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('tooltips.testExecutions')}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                            }}
                          >
                            <CheckCircleIcon fontSize="small" />
                            {t('table.executionCount')}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">{t('table.completionRate')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statsData.tenants.map((tenant) => {
                      const completionRate = getCompletionRate(
                        tenant.summary.enrollments.completed,
                        tenant.summary.enrollments.total
                      )
                      return (
                        <TableRow key={tenant.tenantId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {tenant.tenantName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {tenant.tenantSlug}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={tenant.isActive ? t('status.active') : t('status.inactive')}
                              color={tenant.isActive ? 'primary' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {tenant.summary.users.active} / {tenant.summary.users.total}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{tenant.summary.complianceRules}</TableCell>
                          <TableCell align="right">{tenant.summary.tests}</TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                              alignItems="center"
                            >
                              <Tooltip title={t('tooltips.completed')}>
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label={tenant.summary.enrollments.completed}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </Tooltip>
                              <Tooltip title={t('tooltips.pending')}>
                                <Chip
                                  icon={<ScheduleIcon />}
                                  label={tenant.summary.enrollments.pending}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                              </Tooltip>
                              {tenant.summary.enrollments.overdue > 0 && (
                                <Tooltip title={t('tooltips.overdue')}>
                                  <Chip
                                    icon={<WarningIcon />}
                                    label={tenant.summary.enrollments.overdue}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="secondary.main">
                              {tenant.summary.testResults}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={completionRate}
                                sx={{ width: 60, height: 8, borderRadius: 1 }}
                                color={completionRate >= 80 ? 'primary' : 'secondary'}
                              />
                              <Typography variant="body2" sx={{ minWidth: 40 }}>
                                {completionRate}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button
                  size="small"
                  onClick={() => {
                    setStatsData(null)
                    fetchStats()
                  }}
                  disabled={statsLoading}
                >
                  {t('actions.refreshStats')}
                </Button>
              </Box>
            </>
          ) : (
            <Typography color="text.secondary">{t('stats.noData')}</Typography>
          )}
        </Paper>
      )}

      {/* テナント一覧 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 3,
        }}
      >
        {tenants.map((tenant) => (
          <Card key={tenant.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Stack spacing={2}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {tenant.name}
                  </Typography>
                  <Chip
                    label={tenant.isActive ? t('status.active') : t('status.inactive')}
                    color={tenant.isActive ? 'primary' : 'default'}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {t('card.slug')}: {tenant.slug}
                </Typography>

                {tenant.domain && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {tenant.domain}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {tenant._count?.users || 0} {t('card.users')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AssignmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {tenant._count?.complianceRules || 0} {t('card.rules')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <QuizIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {tenant._count?.tests || 0} {t('card.tests')}
                    </Typography>
                  </Box>
                </Box>

                {/* 契約プラン表示 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <PaymentIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('card.plan')}:
                  </Typography>
                  <Chip
                    label={tenant.subscription?.billingPlan?.nameJa || t('card.freePlan')}
                    size="small"
                    color={
                      tenant.subscription?.billingPlan?.planType === 'BASIC'
                        ? 'primary'
                        : tenant.subscription?.billingPlan?.planType === 'CUSTOM'
                          ? 'secondary'
                          : 'default'
                    }
                    variant="outlined"
                  />
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {t('card.created')}:{' '}
                  {format(new Date(tenant.createdAt), 'yyyy/MM/dd HH:mm', { locale })}
                </Typography>
              </Stack>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => handleEditTenant(tenant)}
              >
                {t('actions.edit')}
              </Button>
              <Button
                size="small"
                startIcon={<PaymentIcon />}
                onClick={() => handleOpenPlanDialog(tenant)}
                color="primary"
              >
                {t('actions.changePlan')}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      {tenants.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <BusinessIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('empty.description')}
          </Typography>
        </Box>
      )}

      {/* 新規作成ダイアログ */}
      <Dialog open={createDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {t('dialog.createTitle')}
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('form.name')}
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              fullWidth
              required
              helperText={t('form.nameHelper')}
            />
            <TextField
              label={t('form.slug')}
              value={formData.slug}
              onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
              fullWidth
              required
              helperText={t('form.slugHelper')}
            />
            <TextField
              label={t('form.description')}
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              helperText={t('form.descriptionHelper')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label={t('form.isActive')}
            />

            {/* CORPORATE_ADMIN作成セクション */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.createAdmin}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, createAdmin: e.target.checked }))
                  }
                />
              }
              label={t('form.createAdmin')}
            />

            {formData.createAdmin && (
              <Box
                sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <Typography variant="subtitle2" gutterBottom color="primary">
                  {t('form.adminInfo')}
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label={t('form.adminEmail')}
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, adminEmail: e.target.value }))
                    }
                    fullWidth
                    required={formData.createAdmin}
                    helperText={t('form.adminEmailHelper')}
                  />
                  <TextField
                    label={t('form.adminName')}
                    value={formData.adminName}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, adminName: e.target.value }))
                    }
                    fullWidth
                    required={formData.createAdmin}
                    helperText={t('form.adminNameHelper')}
                  />
                  <TextField
                    label={t('form.department')}
                    value={formData.adminDepartment}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, adminDepartment: e.target.value }))
                    }
                    fullWidth
                    helperText={t('form.departmentHelper')}
                  />
                  <TextField
                    label={t('form.position')}
                    value={formData.adminPosition}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, adminPosition: e.target.value }))
                    }
                    fullWidth
                    helperText={t('form.positionHelper')}
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common:buttons.cancel')}</Button>
          <Button
            onClick={handleCreateTenant}
            variant="contained"
            disabled={submitting}
            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            {submitting ? <CircularProgress size={20} /> : t('common:buttons.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {t('dialog.editTitle')}
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('form.name')}
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label={t('form.slug')}
              value={formData.slug}
              fullWidth
              disabled
              helperText={t('form.slugDisabledHelper')}
            />
            <TextField
              label={t('form.description')}
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label={t('form.isActive')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common:buttons.cancel')}</Button>
          <Button
            onClick={handleUpdateTenant}
            variant="contained"
            disabled={submitting}
            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            {submitting ? <CircularProgress size={20} /> : t('common:buttons.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* プラン変更ダイアログ */}
      <Dialog
        open={planDialogOpen}
        onClose={() => {
          setPlanDialogOpen(false)
          setSelectedPlanTenant(null)
          setSelectedPlanId('')
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {t('dialog.changePlanTitle')}
            <IconButton
              onClick={() => {
                setPlanDialogOpen(false)
                setSelectedPlanTenant(null)
                setSelectedPlanId('')
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {selectedPlanTenant && (
              <Alert severity="info">
                {t('dialog.changePlanInfo', { tenantName: selectedPlanTenant.name })}
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('form.currentPlan')}
              </Typography>
              <Chip
                label={selectedPlanTenant?.subscription?.billingPlan?.nameJa || t('card.freePlan')}
                color="default"
                variant="outlined"
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel id="plan-select-label">{t('form.newPlan')}</InputLabel>
              <Select
                labelId="plan-select-label"
                value={selectedPlanId}
                label={t('form.newPlan')}
                onChange={(e) => setSelectedPlanId(e.target.value)}
              >
                {billingPlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{plan.nameJa}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({plan.planType})
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPlanDialogOpen(false)
              setSelectedPlanTenant(null)
              setSelectedPlanId('')
            }}
          >
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleUpdatePlan}
            variant="contained"
            disabled={submitting || !selectedPlanId}
            color="primary"
          >
            {submitting ? <CircularProgress size={20} /> : t('actions.changePlan')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default TenantManagementPage
